import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Tip } from '@/features/inspiration/data/tips';

/**
 * SI-Inspiration Variant Y (Sebastian Gate-1 lock 2026-05-11).
 *
 * Collapsible "Alle Tipps anzeigen" expander rendered BELOW the
 * 3-tip trio on the Inspiration-Tab. Initial state: collapsed.
 * Toggle reveals the remaining 8 tips as compact rows (emoji +
 * title). Tapping a row expands it inline to show the body; tap
 * again collapses it. Multiple rows can be open simultaneously.
 *
 * Visual treatment: white card with dream-gold border to match the
 * surrounding TipSection / FeatureCard pattern. Compact rows use a
 * lighter divider line between each entry — readable when many are
 * shown collapsed, calm when one or two are expanded.
 */
export interface TipsExpanderProps {
  tips: readonly Tip[];
  className?: string;
  testId?: string;
}

export function TipsExpander({ tips, className, testId }: TipsExpanderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  const toggleRoot = () => setIsOpen((prev) => !prev);
  const toggleRow = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-lg border border-dream-gold bg-dream-white',
        className,
      )}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={toggleRoot}
        aria-expanded={isOpen}
        aria-controls="tips-expander-list"
        className="flex w-full items-center justify-between gap-2 p-4 text-left transition-colors hover:bg-dream-background focus-visible:outline-2 focus-visible:outline-dream-navy/50"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="text-sm font-semibold text-dream-navy">
          {isOpen ? 'Weniger Tipps' : '💡 Alle Tipps anzeigen'}
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-dream-navy" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-dream-navy" aria-hidden />
        )}
      </button>

      {isOpen && (
        <ul
          id="tips-expander-list"
          className="flex flex-col border-t border-dream-gold/40"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {tips.map((tip) => {
            const isRowOpen = expandedIds.has(tip.id);
            return (
              <li key={tip.id} className="border-b border-dream-gold/30 last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleRow(tip.id)}
                  aria-expanded={isRowOpen}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-dream-background focus-visible:outline-2 focus-visible:outline-dream-navy/50"
                  data-testid={testId ? `${testId}-row-${tip.id}` : undefined}
                >
                  <span aria-hidden className="text-base leading-none">
                    {tip.emoji ?? '💡'}
                  </span>
                  <span className="flex-1 text-sm font-medium text-dream-navy">{tip.title}</span>
                  {isRowOpen ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-dream-navy/60" aria-hidden />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-dream-navy/60" aria-hidden />
                  )}
                </button>
                {isRowOpen && (
                  <p
                    className="px-4 pb-3 text-xs leading-5 text-dream-navy opacity-50"
                    data-testid={testId ? `${testId}-body-${tip.id}` : undefined}
                  >
                    {tip.body}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
